import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { useAuth } from '../../auth';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://getclearance-production.up.railway.app/api/v1';

const PRIMITIVES = [
  { id: 'screening.individual', label: 'Screening', category: 'Screening' },
  { id: 'screening.company', label: 'Company Screening', category: 'Screening' },
  { id: 'screening.wallet', label: 'Wallet Screening', category: 'Screening' },
  { id: 'biometrics.face_compare', label: 'Face Match', category: 'Biometrics' },
  { id: 'biometrics.passive_liveness_free', label: 'Liveness Basic', category: 'Biometrics' },
  { id: 'document.ocr', label: 'Document OCR', category: 'Document' },
  { id: 'device_intel.ip_check', label: 'IP Check', category: 'Device Intel' },
  { id: 'device_intel.email_check', label: 'Email Check', category: 'Device Intel' },
  { id: 'compliance.address_verify', label: 'Address Verify', category: 'Compliance' },
];

const DEFAULT_INPUTS = {
  'screening.individual': { name: "Jane CLEAR", birth_date: "1990-01-15", countries: ["US"], threshold: 0.5 },
  'screening.company': { name: "Acme Corporation", jurisdiction: "US" },
  'screening.wallet': { address: "0x1234567890abcdef1234567890abcdef12345678", chain: "ethereum" },
  'biometrics.face_compare': { source_image: "https://example.com/selfie.jpg", target_image: "https://example.com/id.jpg" },
  'biometrics.passive_liveness_free': { image: "https://example.com/face.jpg" },
  'document.ocr': { storage_key: "documents/test-passport.jpg", document_type: "passport" },
  'device_intel.ip_check': { ip_address: "1.1.1.1" },
  'device_intel.email_check': { email: "legitimate@gmail.com" },
  'compliance.address_verify': { street: "1600 Amphitheatre Parkway", city: "Mountain View", state: "CA", postal_code: "94043", country: "US" },
};

export default function ExplorerTab() {
  const { getToken } = useAuth();
  const [selectedPrimitive, setSelectedPrimitive] = useState('screening.individual');
  const [requestBody, setRequestBody] = useState(JSON.stringify(DEFAULT_INPUTS['screening.individual'], null, 2));
  const [response, setResponse] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executionTime, setExecutionTime] = useState(null);
  const [error, setError] = useState(null);

  function handleSelectPrimitive(id) {
    setSelectedPrimitive(id);
    setRequestBody(JSON.stringify(DEFAULT_INPUTS[id] || {}, null, 2));
    setResponse(null);
    setStatus(null);
    setExecutionTime(null);
    setError(null);
  }

  async function handleRun() {
    setLoading(true);
    setResponse(null);
    setStatus(null);
    setExecutionTime(null);
    setError(null);

    let parsed;
    try {
      parsed = JSON.parse(requestBody);
    } catch (e) {
      setError('Invalid JSON: ' + e.message);
      setLoading(false);
      return;
    }

    const startTime = Date.now();

    try {
      const token = await getToken();
      // Backend expects { inputs: {...} } wrapper
      const payload = { inputs: parsed };
      const res = await fetch(`${API_BASE}/primitives/${selectedPrimitive}/invoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const elapsed = Date.now() - startTime;
      setExecutionTime(elapsed);
      setStatus(res.status);

      const data = await res.json();
      setResponse(data);
    } catch (e) {
      const elapsed = Date.now() - startTime;
      setExecutionTime(elapsed);
      setStatus(500);
      setResponse({ error: e.message });
    } finally {
      setLoading(false);
    }
  }

  const selectedLabel = PRIMITIVES.find(p => p.id === selectedPrimitive)?.label || '';

  return (
    <div className="explorer-tab">
      <style>{`
        .explorer-tab {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .explorer-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--border-primary, #1e2128);
        }
        .explorer-header h2 {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
        }
        .explorer-header p {
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
          margin: 4px 0 0;
        }
        .explorer-layout {
          display: grid;
          grid-template-columns: 200px 1fr 1fr;
          gap: 20px;
          min-height: 500px;
        }
        .explorer-sidebar {
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #1e2128);
          border-radius: 12px;
          padding: 12px;
        }
        .explorer-sidebar h3 {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-tertiary, #6b7280);
          margin: 0 0 12px;
          padding: 0 8px;
        }
        .explorer-sidebar ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .primitive-btn {
          display: block;
          width: 100%;
          padding: 8px 12px;
          background: transparent;
          border: none;
          border-radius: 6px;
          text-align: left;
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
        }
        .primitive-btn:hover {
          background: var(--bg-tertiary, #1a1d24);
          color: var(--text-primary, #f0f2f5);
        }
        .primitive-btn.active {
          background: var(--accent-glow-medium, rgba(99, 102, 241, 0.1));
          color: var(--accent-primary, #6366f1);
        }
        .explorer-panel {
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #1e2128);
          border-radius: 12px;
          display: flex;
          flex-direction: column;
        }
        .panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-primary, #1e2128);
        }
        .panel-header h3 {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
        }
        .panel-header .primitive-name {
          font-size: 12px;
          color: var(--text-tertiary, #6b7280);
        }
        .panel-body {
          flex: 1;
          padding: 16px;
          display: flex;
          flex-direction: column;
        }
        .code-textarea {
          flex: 1;
          background: var(--bg-primary, #0a0a0f);
          border: 1px solid var(--border-primary, #1e2128);
          border-radius: 8px;
          padding: 12px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 12px;
          color: var(--text-primary, #f0f2f5);
          resize: none;
          min-height: 300px;
        }
        .code-textarea:focus {
          outline: none;
          border-color: var(--accent-primary, #6366f1);
        }
        .panel-actions {
          display: flex;
          gap: 8px;
          margin-top: 12px;
        }
        .run-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: var(--accent-primary, #6366f1);
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: opacity 0.15s;
          font-family: inherit;
        }
        .run-btn:hover:not(:disabled) {
          opacity: 0.9;
        }
        .run-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .response-json {
          flex: 1;
          background: var(--bg-primary, #0a0a0f);
          border: 1px solid var(--border-primary, #1e2128);
          border-radius: 8px;
          padding: 12px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 12px;
          color: var(--text-primary, #f0f2f5);
          overflow: auto;
          white-space: pre-wrap;
          min-height: 300px;
        }
        .response-placeholder {
          color: var(--text-tertiary, #6b7280);
          font-style: italic;
        }
        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }
        .status-badge.success {
          background: rgba(16, 185, 129, 0.1);
          color: #10B981;
        }
        .status-badge.error {
          background: rgba(239, 68, 68, 0.1);
          color: #EF4444;
        }
        .response-meta {
          display: flex;
          gap: 16px;
          margin-top: 8px;
          font-size: 12px;
          color: var(--text-tertiary, #6b7280);
        }
        .error-msg {
          color: #EF4444;
          font-size: 13px;
          margin-top: 8px;
        }
      `}</style>

      <div className="explorer-header">
        <div>
          <h2>API Explorer</h2>
          <p>Test API primitives directly from the dashboard</p>
        </div>
      </div>

      <div className="explorer-layout">
        <div className="explorer-sidebar">
          <h3>Primitives</h3>
          <ul>
            {PRIMITIVES.map(p => (
              <li key={p.id}>
                <button
                  className={`primitive-btn ${selectedPrimitive === p.id ? 'active' : ''}`}
                  onClick={() => handleSelectPrimitive(p.id)}
                >
                  {p.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="explorer-panel">
          <div className="panel-header">
            <h3>Request</h3>
            <span className="primitive-name">{selectedLabel}</span>
          </div>
          <div className="panel-body">
            <textarea
              className="code-textarea"
              value={requestBody}
              onChange={e => setRequestBody(e.target.value)}
              spellCheck={false}
            />
            {error && <div className="error-msg">{error}</div>}
            <div className="panel-actions">
              <button
                className="run-btn"
                onClick={handleRun}
                disabled={loading}
              >
                <Play size={14} />
                {loading ? 'Running...' : 'Run'}
              </button>
            </div>
          </div>
        </div>

        <div className="explorer-panel">
          <div className="panel-header">
            <h3>Response</h3>
            {status && (
              <span className={`status-badge ${status >= 200 && status < 300 ? 'success' : 'error'}`}>
                {status}
              </span>
            )}
          </div>
          <div className="panel-body">
            {response ? (
              <>
                <pre className="response-json">{JSON.stringify(response, null, 2)}</pre>
                {executionTime !== null && (
                  <div className="response-meta">
                    <span>Execution time: {executionTime}ms</span>
                  </div>
                )}
              </>
            ) : (
              <div className="response-json response-placeholder">
                Select a primitive and click Run to see the response.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
